import classNames from 'classnames'
import React, { useRef, useState } from 'react'
import { OmButton, OmSelect, type OmSelectOption } from '@owma/kit'
import { useTranslation } from 'react-i18next'

import settings from 'popups/Settings'
import ColorButton from '../common/ColorButton'
import { basicColors } from 'utils/ui'
import type { ICSSubscription, RefreshInterval } from 'pages/calendar/modules/CalendarService'
import styles from './style.module.scss'

export type { SubscriptionEditorData } from './types'
import type { SubscriptionEditorData } from './types'

interface SubscriptionEditorProps {
  subscription?: ICSSubscription | null
  onSave: (data: SubscriptionEditorData) => void
  onClose: () => void
  loading?: boolean
}

const SubscriptionEditor: React.FC<SubscriptionEditorProps> = ({
  subscription = null,
  onSave,
  onClose,
  loading = false,
}) => {
  const isEditMode = subscription !== null
  const { t } = useTranslation()
  const locale = settings.getLocale()

  const [url, setUrl] = useState(subscription?.url || '')
  const [displayName, setDisplayName] = useState(subscription?.displayName || '')
  const [color, setColor] = useState(
    subscription?.color || basicColors[Math.floor(Math.random() * basicColors.length)],
  )
  const [refreshInterval, setRefreshInterval] = useState<RefreshInterval>(
    subscription?.refreshInterval || 'no',
  )

  const initialUrl = useRef(subscription?.url || '')
  const initialDisplayName = useRef(subscription?.displayName || '')
  const initialColor = useRef(
    subscription?.color || basicColors[Math.floor(Math.random() * basicColors.length)],
  )
  const initialRefreshInterval = useRef<RefreshInterval>(subscription?.refreshInterval || 'no')

  const isModified = (): boolean => {
    if (isEditMode) {
      if (!displayName.trim()) return false
      return (
        displayName !== initialDisplayName.current ||
        color !== initialColor.current ||
        refreshInterval !== initialRefreshInterval.current
      )
    }
    return url.trim().length > 0
  }

  const handleSubmit = (): void => {
    onSave({
      url: isEditMode ? initialUrl.current : url,
      displayName,
      color,
      refreshInterval,
    })
  }

  const refreshOptions: OmSelectOption<RefreshInterval>[] = [
    { value: 'no', label: t('calendar.subscription.refreshNo') },
    { value: 'every_hour', label: t('calendar.subscription.refreshEveryHour') },
    { value: 'every_day', label: t('calendar.subscription.refreshEveryDay') },
    { value: 'every_week', label: t('calendar.subscription.refreshEveryWeek') },
  ]

  return (
    <div className={styles.editor}>
      <div className={styles.header}>
        <h2>{isEditMode ? t('calendar.subscription.edit') : t('calendar.subscription.new')}</h2>
      </div>
      <div className={styles.content}>
        {!isEditMode && (
          <>
            <p className={styles.description}>{t('calendar.subscription.urlDescription')}</p>
            <div className={classNames(styles.fieldRow, styles.fieldRowNew)}>
              <label className={styles.fieldLabel}>{t('calendar.subscription.calendarUrl')}</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t('calendar.subscription.urlPlaceholder')}
                autoFocus
              />
              <ColorButton color={color} onChange={setColor} />
            </div>
          </>
        )}
        {isEditMode && (
          <>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel}>{t('calendar.subscription.name')}</label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('calendar.edit.calendarNamePlaceholder')}
                autoFocus
              />
              <ColorButton color={color} onChange={setColor} />
            </div>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel}>{t('calendar.subscription.subscribeTo')}</label>
              <input value={subscription.url} disabled title={subscription.url} />
            </div>
            {subscription.lastSynced && (
              <div className={styles.fieldRow}>
                <label className={styles.fieldLabel}>
                  {t('calendar.subscription.lastUpdated')}
                </label>
                <span className={styles.infoText}>
                  {new Date(subscription.lastSynced).toLocaleString(locale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true,
                  })}
                </span>
              </div>
            )}
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel}>{t('calendar.subscription.autoRefresh')}</label>
              <OmSelect
                className={styles.refreshSelect}
                value={refreshInterval}
                onChange={setRefreshInterval}
                options={refreshOptions}
                size="md"
              />
            </div>
          </>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerButtons}>
          <button className={styles.cancelButton} onClick={onClose} disabled={loading}>
            {t('common.button.cancel')}
          </button>
          {isEditMode ? (
            <OmButton
              className={classNames('type1', 'w100', 'h38')}
              disabled={!isModified()}
              onClick={handleSubmit}
            >
              {t('common.button.ok')}
            </OmButton>
          ) : (
            <button
              className={styles.saveButton}
              data-disabled={!isModified() || loading}
              onClick={() => {
                if (isModified() && !loading) {
                  handleSubmit()
                }
              }}
            >
              {loading
                ? t('calendar.subscription.subscribing')
                : t('calendar.subscription.subscribe')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default SubscriptionEditor
